/**
 * Shell, routing and the auth gate.
 *
 * Boot order matters: detect the host first, because inside Teams there is no
 * login step at all and rendering a login form -- even for a moment -- is the
 * exact thing this port is meant to eliminate.
 */
import { Menu, Monitor, Moon, PanelLeft, PanelLeftClose, Sun } from 'lucide-react'
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'

import axios from 'axios'

import trgIcon from './assets/brand/trg-icon.png'
import trgWordmarkBlack from './assets/brand/trg-wordmark-black.png'
import trgWordmarkWhite from './assets/brand/trg-wordmark-white.png'

import { btnGhost } from './components/ui'
import ErrorBoundary from './components/ErrorBoundary'
import { SignOutButton } from './components/SignOutButton'
import ImpersonationBanner from './components/ImpersonationBanner'
import LeaverPayslipsOnly from './components/LeaverPayslipsOnly'
import NoAccessLevel from './components/NoAccessLevel'
import { ForcedPasswordChange } from './components/PasswordChange'
import PlatformConsole from './components/PlatformConsole'
import SignIn from './components/SignIn'
import ComingSoon from './pages/ComingSoon'
// Dashboard is the landing route (`/`) -- it stays eager. Splitting it would
// move first-paint cost rather than remove it.
import Dashboard from './pages/Dashboard'
// MySchedule is returned directly by the self-service-only auth-gate branch
// above, outside <Routes>/<Suspense>, so it stays eager rather than needing its
// own Suspense boundary. It is ALSO the `/roster/my`
// route element, embedded in the shell, for managers and staff.
import MySchedule from './pages/roster/MySchedule'
import { PublicDashboard, ShareOpen } from './components/PublicView'
import { isPublicOnly, shareTokenFromPath } from './lib/shareLinks'
import { publicStockMode } from './lib/oreas'
import { holdsTick, readsInventory } from './pages/inventory/inventoryTab'
import NoRosterRole from './pages/roster/NoRosterRole'
// Everything below is a <Route> element only, lazy-loaded: src/pages is roughly
// a third of the bundle, worth deferring to the route that needs it.
const Profile = lazy(() => import('./pages/Profile'))
const HotDesk = lazy(() => import('./pages/hotdesk/HotDesk'))
const Laptops = lazy(() => import('./pages/inventory/Laptops'))
const OreasStock = lazy(() => import('./pages/public/OreasStock'))
const EmployeeDetail = lazy(() => import('./pages/hr/EmployeeDetail'))
const Company = lazy(() => import('./pages/company/Company'))
const OrganizationPage = lazy(() => import('./pages/organization/OrganizationPage'))
const MyPanel = lazy(() => import('./pages/me/MyPanel'))
const Payroll = lazy(() => import('./pages/payroll/Payroll'))
const RosterSection = lazy(() => import('./pages/roster/RosterSection'))
const Schedule = lazy(() => import('./pages/roster/Schedule'))
const RosterPeople = lazy(() => import('./pages/roster/People'))
const Bus = lazy(() => import('./pages/roster/Bus'))
const Hse = lazy(() => import('./pages/roster/Hse'))
const Performance = lazy(() => import('./pages/roster/Performance'))
const Cost = lazy(() => import('./pages/roster/Cost'))
const RosterSetup = lazy(() => import('./pages/roster/Setup'))
const RosterGear = lazy(() => import('./pages/roster/Gear'))
const Leave = lazy(() => import('./pages/time/Leave'))
const ContractorLog = lazy(() => import('./pages/contractorLog/ContractorLog'))
const TimeLogging = lazy(() => import('./pages/time/TimeLogging'))
const Config = lazy(() => import('./pages/admin/Config'))
// The bank list is a Sales page now; the file stays in `pages/admin/`.
const Banks = lazy(() => import('./pages/admin/Banks').then((m) => ({ default: m.BanksTab })))
const Audit = lazy(() => import('./pages/admin/Audit'))
const Automation = lazy(() => import('./pages/admin/Automation'))
import {
  APPS,
  publicApps,
  appForPath,
  bandLanding,
  firstVisibleModule,
  isVisible,
  modulePath,
  NAV_GROUPS,
  type AppDef,
} from './lib/apps'
import { navTabsFor } from './lib/navTabs'
import { isRosterManager, rosterVisible, scheduleVisible } from './lib/roster/access'
import { rosterPhoneRedirect } from './lib/roster/phoneView'
import { legacyCompanyPath, legacyMePath } from './lib/sectionTabs'
import {
  PHONE_QUERY,
  getStoredRailPin,
  hasNavigated,
  isRailDocked,
  markNavigated,
  storeRailPin,
  type RailPin,
} from './lib/nav'
import { pageGutterClass } from './lib/pageWidth'

const SalesSection = lazy(() => import('./pages/sales/SalesSection'))
const SalesList = lazy(() => import('./pages/sales/SalesList'))
const SalesEditor = lazy(() => import('./pages/sales/SalesEditor'))
const Clients = lazy(() => import('./pages/reference/Clients'))
const ClientDetail = lazy(() => import('./pages/sales/ClientDetail'))
const PriceList = lazy(() => import('./pages/reference/PriceList'))
const ActivityFund = lazy(() => import('./pages/operations/ActivityFund'))
const RequestsHub = lazy(() => import('./pages/operations/RequestsHub'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const UiKitGallery = import.meta.env.DEV ? lazy(() => import('./pages/dev/UiKitGallery')) : null
import { setLanguage } from './i18n'
import { errorMessage, isPlatformOperatorRefusal } from './lib/api'
import { accountNotSetUpAddress } from './lib/accountNotSetUp'
import { meGate, teamsAuthScreen } from './lib/authScreen'
import { isAccountInactive, isCompanyDeactivated, showForcedChange } from './lib/password'
import { useUnreadNotifications } from './components/NotificationCenter'
import { navBadgeText, useApprovalsAwaiting } from './lib/dashboard'
import { canWriteOreas, OREAS_REQUESTS_LINK } from './lib/oreas'
import { useOreasAwaiting } from './lib/oreasQueries'
import { BRAND } from './lib/brand'
import { can } from './lib/types'
import { movedConfigTab } from './pages/admin/configMoves'
import { mayOpenConfig } from './pages/admin/configTabs'
import { mayEditHr, mayOpenHr } from './lib/hrAccess'
import { useBrandLogo } from './lib/organization'
import { useMe } from './lib/queries'
import { BACKLOG_TAB_HREF } from './pages/time/scopes'
import { leaveHref } from './pages/time/leaveLink'
import {
  detectHost,
  hasAnyBrowserSession,
  signOutEverywhere,
  watchTheme,
  type AuthMode,
} from './lib/teams'
import {
  applyTheme,
  getStoredThemeMode,
  resolveIsDark,
  storeThemeMode,
  THEME_MODES,
  type ThemeMode,
} from './lib/theme'

/** One page inside an app, as the rail flyout and the in-page tab bar draw it. */
type NavItemDef = {
  to: string
  label: string
  icon?: React.ComponentType<{ className?: string }>
}

/** Row height the flyout assumes when keeping itself on screen. */
const FLYOUT_ROW = 36

/** The rail footer's language control. Two-letter native codes, not flag
 *  emoji -- measured on this user's own machine (Chrome/Windows 11) at 41.2px
 *  for a two-codepoint flag versus 22.5px for one regional-indicator letter, a
 *  1.83 ratio that renders as two boxed letters, not a flag: Windows ships no
 *  flag glyphs. `name` carries the full word for `title`/`aria-label` so nothing
 *  is lost for a screen reader. */
const LANGUAGES = [
  { code: 'mn', short: 'МН', name: 'Монгол' },
  { code: 'en', short: 'EN', name: 'English' },
  { code: 'ru', short: 'РУ', name: 'Русский' },
] as const

/** Abilities that CONFINE their holder rather than making them an ordinary
 *  user of the app, mirroring `core.permissions.
 *  SELF_SERVICE_ABILITIES` on the backend. `roster.self` is the only entry
 *  today: a level whose whole grant is a subset of this set reaches nothing
 *  but `GET /api/roster/me` (`deps.deny_pending`), so the shell must not be
 *  the thing that greets them -- see the check below and `MySchedule`. */
const SELF_SERVICE_ABILITIES = new Set(['roster.self'])

function AppShell() {
  const [host, setHost] = useState<AuthMode | null>(null)
  // The user's explicit choice; `systemIsDark` is what "system" resolves to.
  const [themeMode, setThemeMode] = useState<ThemeMode>(getStoredThemeMode)
  const [systemIsDark, setSystemIsDark] = useState(false)
  const [railPin, setRailPin] = useState<RailPin | null>(getStoredRailPin)
  // Narrow enough that a docked rail would take most of the width. Read from
  // `matchMedia` rather than a CSS class because it decides more than a width:
  // the labels' opacity and the footer's `visibility` follow `docked` too, and
  // forcing the box narrow in CSS alone would leave the footer's <select> and
  // buttons tabbable but clipped out of sight -- the exact thing the note on
  // `footerOnlyWide` says is worse than hiding them.
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches,
  )
  // The phone's stand-in for the pin: the rail open OVER the page, until the
  // next navigation. Only consulted at phone width -- see `railOpen`.
  const [drawer, setDrawer] = useState(false)
  // Has the user gone anywhere yet this session? The rail opens on the page
  // they land on and collapses to icons for good once they leave it.
  const [navigated, setNavigated] = useState(hasNavigated)
  const landedAt = useRef(useLocation().pathname)
  const navScroll = useRef<HTMLElement | null>(null)
  // The sub-page flyout: which app it belongs to, and where to put it.
  const [flyout, setFlyout] = useState<{ key: string; top: number; items: NavItemDef[] } | null>(
    null,
  )
  const flyoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set when Escape hands focus back to an app row, so that focus does not
  // reopen the flyout it just closed.
  const skipFocusOpen = useRef(false)
  const { data: user, isLoading, isError, error, refetch } = useMe()
  const { pathname, search } = useLocation()
  const { t, i18n } = useTranslation()
  const isCustomer = user?.is_customer === true
  const brandLogo = useBrandLogo(user?.id, isCustomer)

  const isDark = resolveIsDark(themeMode, systemIsDark)

  // Whether the rail sits open without anyone hovering it: an explicit pin if
  // there is one, otherwise open only on the session's first page -- and never
  // on a phone, where 240px of rail is most of the screen. See `isRailDocked`.
  const docked = isRailDocked(railPin, !navigated, isPhone)

  // AN OPEN FLYOUT HOLDS THE RAIL OPEN. A flyout can now only open once the
  // rail is already docked or drawer-open (see `openFlyout`'s guard) -- but
  // that state can still change under it, e.g. Ctrl+B unpinning the rail while
  // the pointer is over the flyout panel. Without this term the rail would
  // collapse to icons under a panel that is still open, stranding it beside a
  // 56px strip. `docked` still means "the user's own choice", which is what
  // the pin toggle and the stored preference read.
  //
  // ON A PHONE THE DRAWER HOLDS IT OPEN INSTEAD. `docked` is what reserves
  // layout width, and 240px of that on a 390px screen leaves 118px of page --
  // so a phone never docks. But the pin is "the entire story on a touch screen,
  // where there is no hover to expand with" (see the button below), and simply
  // making it inert would take the only way a touch user reads the labels. So
  // on a phone the same button opens the rail as an OVERLAY -- the <aside> is
  // already `fixed`, so widening it costs the page nothing -- and the drawer
  // closes on navigation, which a pinned preference would not.
  const railOpen = docked || drawer || flyout !== null

  /** What the pin button reports and undoes -- the open states a click made,
   *  which is not the hover-driven flyout one. */
  const railToggled = docked || drawer

  const toggleRail = useCallback(() => {
    // The pin is a stored preference about a docked rail; a phone has no docked
    // rail to have a preference about. Writing one would also follow the user
    // back to their desk, where they never chose it.
    if (isPhone) {
      setDrawer((open) => !open)
      return
    }
    const next: RailPin = docked ? 'collapsed' : 'expanded'
    storeRailPin(next)
    setRailPin(next)
  }, [docked, isPhone])

  // A flyout is anchored to a row of a list that is about to change; it must
  // not outlive the page it was opened from. The drawer goes with it: on a
  // phone it covers most of the page, and the reason you opened it was to
  // leave for somewhere else.
  useEffect(() => {
    setFlyout(null)
    setDrawer(false)
  }, [pathname, search])

  // Leaving phone width abandons the drawer -- otherwise a rotation to landscape
  // leaves a rail that is open for a reason that no longer exists and that the
  // pin, now writing a preference again, cannot close.
  useEffect(() => {
    if (!isPhone) setDrawer(false)
  }, [isPhone])

  // The first move off the landing page ends the one-off open state, for the
  // rest of the session.
  useEffect(() => {
    if (!navigated && pathname !== landedAt.current) {
      markNavigated()
      setNavigated(true)
    }
  }, [navigated, pathname])

  // BACK TO THE TOP WHENEVER THE RAIL NARROWS. The nav is a scroller, and a
  // scroll position set while the rail was open survives the collapse -- so the
  // dashboard and the first band could sit above the top edge of a rail that is
  // now nothing but icons, which reads as icons having gone missing. Collapsed,
  // the list is meant to be visible in full from the first icon down.
  useEffect(() => {
    if (!docked) navScroll.current?.scrollTo({ top: 0 })
  }, [docked, pathname])

  useEffect(() => {
    // Track the host's own theme -- the Teams client theme inside Teams, the OS
    // colour scheme in a plain browser. This is what the "system" mode follows.
    const onSystem = (theme: string) =>
      // Treat Teams high-contrast as dark for styling purposes.
      setSystemIsDark(theme === 'dark' || theme === 'contrast')

    let cleanup: (() => void) | undefined
    // detectHost() also consumes the MSAL redirect on the way back from
    // Microsoft, so this must settle before deciding what to render.
    void detectHost().then((mode) => {
      setHost(mode)
      if (mode === 'teams') {
        void watchTheme(onSystem)
      } else {
        const media = window.matchMedia('(prefers-color-scheme: dark)')
        setSystemIsDark(media.matches)
        const onChange = (event: MediaQueryListEvent) => setSystemIsDark(event.matches)
        media.addEventListener('change', onChange)
        cleanup = () => media.removeEventListener('change', onChange)
      }
    })
    return () => cleanup?.()
  }, [])

  // Apply the resolved theme whenever the mode or the system theme changes.
  useEffect(() => {
    applyTheme(isDark)
  }, [isDark])

  // Follow the width. A Teams tab is resized by the app around it and a phone
  // is rotated, so this is not a one-shot read at mount.
  useEffect(() => {
    const media = window.matchMedia(PHONE_QUERY)
    setIsPhone(media.matches)
    const onChange = (event: MediaQueryListEvent) => setIsPhone(event.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  // Ctrl/Cmd+B pins and unpins the rail — the shortcut every editor already
  // uses for a sidebar, so it is the one people try first. Bound on the window
  // rather than the rail: the point is to reach it without pointing at it.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'b') {
        event.preventDefault()
        toggleRail()
        return
      }
      // The backdrop's tap-to-dismiss equivalent for a keyboard user -- only
      // meaningful while the phone drawer is actually open as an overlay.
      if (event.key === 'Escape' && isPhone && drawer) {
        setDrawer(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [toggleRail, isPhone, drawer])

  function chooseTheme(mode: ThemeMode) {
    storeThemeMode(mode)
    setThemeMode(mode)
  }

  if (host === null || isLoading) {
    return <Splash />
  }

  // Outside Teams, send the user to Microsoft to sign in. Inside Teams a
  // failure here is a configuration problem (consent, manifest, audience), not
  // a missing login -- so it says so rather than showing a sign-in button that
  // cannot help.
  //
  // `isError` ALONE IS NOT "THE SESSION IS GONE". A failed background
  // refetch of /me (a tab refocus over a dropped connection, a 5xx) keeps the
  // cached `user` and flips `isError`; gating on it unmounted the whole app --
  // open dialogs closed, masked payroll figures re-hid -- for a person who was
  // still signed in. See `meGate`: with a user held, only a 401/403 gates.
  const meStatus = axios.isAxiosError(error) ? error.response?.status : undefined
  const gate = meGate(!!user, isError, meStatus)
  if (gate === 'gate' || !user) {
    // A PLATFORM OPERATOR, NOT A USER. This outranks every branch
    // below, including the Teams one: the API read a valid token and named
    // exactly who this is, so the host it arrived from changes nothing. They
    // hold no `person` row, no organization and no abilities, so there is
    // nothing for the shell to render -- Sales, HR and Time all need a tenant.
    // The console replaces the app rather than adding a page to it.
    //
    // KEYED ON `detail.code`, NOT ON THE 403 -- an expired token answers 403
    // too, and confusing the two would show this shell to an ordinary user.
    // See `isPlatformOperatorRefusal`, which reads the payload directly rather
    // than going through `errorMessage()`.
    if (isPlatformOperatorRefusal(error)) return <PlatformConsole />

    // Why /me failed decides what to say.
    const status = meStatus

    // Inside Teams too -- a 403 is a missing/inactive account and a 5xx
    // an outage, neither a manifest problem. See lib/authScreen.
    if (host === 'teams') {
      const screen = teamsAuthScreen(status, axios.isAxiosError(error) && !error.response)
      if (screen === 'no-account')
        return (
          <NoAccountError detail={errorMessage(error)} notSetUpAddress={accountNotSetUpAddress(error)} />
        )
      if (screen === 'unreachable') return <ApiUnreachableError onRetry={() => void refetch()} />
      return (
        <TeamsAuthError
          detail={error ? errorMessage(error) : undefined}
          onRetry={() => void refetch()}
        />
      )
    }

    // 403 is conclusive on its own: the API read a valid token and still
    // refused. Deliberately not gated on hasAnyBrowserSession() -- ssoSilent
    // populates the MSAL account as a side effect of the very request that
    // failed, so whether a session is visible here depends on render timing.
    if (status === 403)
      return <NoAccountError detail={errorMessage(error)} notSetUpAddress={accountNotSetUpAddress(error)} />

    // Signed in, but nothing came back: API down, DNS/CORS failure, or a 5xx.
    // Reporting that as a missing account sends the user to the wrong person
    // and hides an outage. `status === undefined` is the no-response case.
    if (hasAnyBrowserSession() && (status === undefined || status >= 500)) {
      return <ApiUnreachableError onRetry={() => void refetch()} />
    }

    // 401, or no session at all -- the interceptor has already dropped any
    // cached user, so the sign-in gate is the right destination.
    return <SignIn onSignedIn={() => void refetch()} />
  }

  // Signed in, holding a level that grants nothing. The API refuses these
  // people on every route except /api/auth/me, so rendering the shell would be
  // a screen of failing panels with nothing to explain them. This is not an
  // error state -- see components/NoAccessLevel and
  // docs/design/DATABASE.md.
  //
  // THE TEST IS THE ABILITY SET, NOT A TIER NAME, AND THAT IS NOPEND. It used
  // to read `user.tier === 'pending'`; that value no longer exists -- the level
  // was retired and `access_level` no longer has a row for it, so nothing can
  // hold the slug. What replaces it mirrors the server's own question
  // (`permissions.has_no_access_level`) rather than restating it: does this
  // person's level grant anything? Both fields ship on `UserOut`.
  //
  // `is_admin` FIRST, AND IT IS NOT BELT AND BRACES. Admin is a master switch
  // resolved in code, seeded with a deliberately EMPTY ability list -- so on any
  // path where `abilities` arrives unpopulated, testing emptiness alone would
  // lock out every administrator including the one who has to fix it. The
  // backend exempts admin at the same point and for the same reason.
  //
  // A person with `tier` NULL does NOT arrive here: `current_user` refuses them
  // before /me answers, and `NoAccountError` below is their screen.
  // A LEAVER INSIDE THEIR 90-DAY PAYSLIP WINDOW, AND THIS IS ASKED
  // BEFORE THE TWO CHECKS BELOW, mirroring the order `deps.deny_pending` uses
  // on the server and for the identical reason. A leaver's access level is
  // whatever it was on their last day and may grant nothing at all, so testing
  // the ability set first would hand them `NoAccessLevel` -- a screen telling
  // them to ask an administrator for access nobody is going to re-grant to
  // somebody who has left, while hiding the payslips they ARE entitled to.
  //
  // NO `is_admin` GUARD ON THIS ONE, unlike both checks below, and the
  // asymmetry is deliberate. Those two infer confinement from an EMPTY
  // `abilities` array, which an administrator's is seeded with -- so they need
  // the master switch or they lock out the person who has to fix it. This flag
  // is not inferred: the server sets it only after reading a real
  // `resignation_date`, so an admin can only ever carry it while VIEWING AS a
  // leaver, which is exactly when this screen is the right answer.
  // AN ADMINISTRATOR-SET PASSWORD, AND THIS IS ASKED FIRST. The server
  // answers 403 `password_change_required:` on every route but /me, the
  // change route and logout until it is changed, so any screen below would be
  // one of failing panels. Not while viewing as somebody: the change route
  // acts on the signed-in administrator (`showForcedChange`).
  if (showForcedChange(user)) return <ForcedPasswordChange user={user} />

  if (user.is_leaver_confined) return <LeaverPayslipsOnly user={user} />

  if (!user.is_admin && user.abilities.length === 0) return <NoAccessLevel user={user} />

  // Signed in, holding ONLY a self-service ability. The API
  // confines this principal to `GET /api/roster/me` and refuses every other
  // route it gates (`deps.deny_pending`'s second half, not its first: this is
  // not "grants nothing", it is "grants nothing beyond looking yourself up").
  // The shell would still render every nav entry, dashboard tile and the
  // notification bell unconditionally, and every one of them would then 403 --
  // the same "screen of dead links" the check above exists to avoid one level
  // up. So this person never reaches the shell either; they get the one page
  // they can actually use. `is_admin` first, for the same reason as above: an
  // admin's abilities array is seeded empty, and the subset test would
  // otherwise read that as self-service-only.
  if (
    !user.is_admin &&
    user.abilities.length > 0 &&
    user.abilities.every((a) => SELF_SERVICE_ABILITIES.has(a))
  )
    return <MySchedule user={user} />

  const publicOnly = isPublicOnly(user)

  // Which app the current URL belongs to (the `dashboard` entry on `/`).
  const activeApp = appForPath(pathname)

  // Modules of the active app the user may actually see. A module the user
  // lacks the ability for is hidden AND unrouted, so a typed URL falls through
  // to the redirect rather than rendering a page that only 403s.
  //
  // ROSTER IS THE ONLY APP LEFT WITH MODULES, now that Finance and
  // Administration are bands of top-level apps. That does not make this
  // machinery dead: Roster's six modules genuinely are one app -- they share a
  // single versioned document, which is exactly the case the second level
  // exists for, and is why it did not get flattened with the other two.
  const holds = (ability: string) => can(user, ability)
  // The Roster shell is for a roster role, not an ability.
  const rosterOk = rosterVisible(user)
  const scheduleOk = scheduleVisible(user)
  const rosterPhoneTarget = rosterPhoneRedirect(pathname, isPhone, rosterOk)
  // Roster Setup is the manager's (a People-page tick), not the ability's.
  const rosterManager = isRosterManager(user)
  const contractorLogOk = user.contractor_log === true
  const inventoryOk = { reader: readsInventory(user), assets: holdsTick(user, 'assets') }
  const navItemsFor = (app: AppDef): NavItemDef[] => {
    // An app the user may not see has no module tabs either -- a
    // member on a `/roster` URL now gets a page saying why, not a strip of
    // tabs that each lead back to it.
    if (!isVisible(app, holds, rosterOk, false, contractorLogOk, scheduleOk, inventoryOk)) return []
    return app.modules
      .filter((m) => isVisible(m, holds, rosterOk, rosterManager, false, false, inventoryOk))
      .map((m) => ({ to: modulePath(app, m), label: t(m.labelKey), icon: m.icon }))
  }

  // Which module a URL is actually on: the LONGEST module path it sits under.
  // Sales' own list is the module with an empty path, so its route is the app
  // prefix and every sibling starts with it -- a plain `startsWith` would light
  // up Documents and Clients at the same time on /finance/sales/clients.
  const activeModule = (items: { to: string }[]) =>
    items
      .filter((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0]?.to

  /** What the flyout lists for an app: its modules, or else its page's own inner tabs. */
  const flyoutItemsFor = (app: AppDef): NavItemDef[] => {
    const modules = navItemsFor(app)
    if (modules.length > 0 || !isVisible(app, holds, rosterOk, false, contractorLogOk, scheduleOk, inventoryOk)) return modules
    return navTabsFor(app.key, app.prefix, user).map((tab) => ({ to: tab.to, label: t(tab.labelKey) }))
  }

  /** The current app's pages, for the tab bar over the page itself. */
  const pageTabs = activeApp ? navItemsFor(activeApp) : []

  // Opening and closing the flyout. The close is DELAYED because the pointer
  // has to cross the gap between the tab and the panel to reach it, and a
  // panel that vanishes on the way there cannot be clicked. Entering the panel
  // cancels the pending close.
  const openFlyout = (key: string, row: HTMLElement, items: NavItemDef[]) => {
    // A HOVER PREVIEW HAS NO TOUCH EQUIVALENT, and `onMouseEnter` still fires
    // on a tap. Without this guard, touching an app icon on a phone parked the
    // panel at `left-[244px]` -- past the right edge of a 390px viewport -- and
    // held the rail open at 240px behind it, over the page it was previewing.
    // Nothing is lost: the panel lists an app's pages, and `ModuleTabs` puts
    // that same list on the page itself, where it scrolls.
    //
    // COLLAPSED, THE RAIL ANSWERS "WHICH APP" ONLY (see the comment at the
    // band map below) -- a flyout is "which page", which only makes sense once
    // "which app" is already answered by a docked or drawer-open rail. Without
    // this guard, `flyout !== null` still flips `railOpen` (see its definition)
    // and widens the rail through React state -- the exact hover-widens-the-
    // rail behaviour the tooltip was built to replace, just routed around it
    // for the two apps (Sales, Roster) that have a flyout at all.
    if (isPhone || items.length === 0 || !railToggled) return
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current)
    const { top } = row.getBoundingClientRect()
    const height = items.length * FLYOUT_ROW + 8
    setFlyout({ key, items, top: Math.max(8, Math.min(top, window.innerHeight - height - 8)) })
  }
  const closeFlyoutSoon = () => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current)
    flyoutTimer.current = setTimeout(() => setFlyout(null), 140)
  }
  const keepFlyout = () => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current)
  }

  // Where an app's bare prefix sends this user. Asked by app key rather than
  // read off the active app, so it answers correctly for an app the user is
  // not currently inside -- which is the whole point once the nav links every
  // app at its prefix. Falls back to the dashboard when nothing inside is open
  // to them.
  const landing = (key: string) => {
    const app = APPS.find((a) => a.key === key)
    return (app && firstVisibleModule(app, holds, rosterOk, rosterManager)) ?? '/'
  }

  // An app the user cannot enter is hidden rather than shown and then refused.
  // Payroll uses the ability form: `salary.view` can be granted to the payroll
  // officer without making them an administrator.
  const visibleApps = publicOnly
    ? publicApps(APPS)
    : APPS.filter((a) => isVisible(a, holds, rosterOk, false, contractorLogOk, scheduleOk, inventoryOk))

  // The band marker above the first group that actually renders has nothing
  // above it to separate from, so it must be suppressed -- but "first" means
  // first *drawn*, not first in NAV_GROUPS: a group with no visible apps is
  // skipped, so for a non-admin user the first drawn group is often not
  // NAV_GROUPS[0].
  const firstVisibleGroupKey = NAV_GROUPS.find(({ key }) =>
    visibleApps.some((a) => a.group === key),
  )?.key

  // Where the bare /finance and /admin land now that neither owns a page of its
  // own: the first app of that band this user may open, in sidebar order.
  const financeLanding = bandLanding(visibleApps, '/finance')
  const adminLanding = bandLanding(visibleApps, '/admin')

  // WHAT SURVIVES AT EACH WIDTH IS A QUESTION ABOUT VISIBILITY, NOT LAYOUT.
  // The rail's content is laid out at the docked width at every width and
  // clipped (see the content box below), so nothing here may switch `display`
  // or `position`: that is what used to re-flow the whole rail mid-animation.
  // Opacity leaves the box exactly where it was, and — unlike `hidden` or
  // `sr-only` — leaves the text in the accessibility tree, so each icon keeps
  // its accessible name at every width.
  //
  // `group-has-[:focus-visible]` keys off the `group` class on the <aside>, so
  // this follows the rail without any React state, and fades over the same
  // 150ms the width takes. There is no `group-hover` counterpart -- hovering a
  // collapsed rail no longer reveals the label text, only that item's tooltip.
  //
  // FOCUS-VISIBLE, NOT FOCUS-WITHIN. A clicked link keeps focus after the
  // navigation, so `:focus-within` held the rail open on every page you reached
  // by clicking one -- which is precisely the "it never collapses" complaint.
  // `:focus-visible` is set for keyboard focus and not for a mouse click, so
  // tabbing into the rail still opens it and clicking through it does not.
  const fade = 'transition-opacity duration-150 motion-reduce:transition-none'
  const wideOnly = railOpen
    ? fade
    : `${fade} opacity-0 group-has-[:focus-visible]:opacity-100`
  const narrowOnly = railOpen
    ? `${fade} opacity-0`
    : `${fade} group-has-[:focus-visible]:opacity-0`
  // The footer is the one block that must also leave the tab order when it is
  // out of sight: it holds a <select> and two buttons, and a control you cannot
  // see but can still tab into is worse than one that is simply gone.
  // `visibility` does that and still reserves the box, so the rows inside it
  // are laid out at 240px and can no longer collide.
  const footerOnlyWide = railOpen
    ? ''
    : 'invisible opacity-0 group-has-[:focus-visible]:visible group-has-[:focus-visible]:opacity-100'
  const labelClass = wideOnly

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100">
      {/* The rail is `fixed`, so THIS is what occupies layout width. Hover
          expansion then floats over the page rather than reflowing it -- the
          schedule grid, Payroll and the wide admin tables would otherwise
          re-lay-out on every pass of the pointer. */}
      <div
        aria-hidden="true"
        className={`shrink-0 transition-[width] duration-150 motion-reduce:transition-none ${
          isPhone ? 'w-0' : docked ? 'w-60' : 'w-14'
        }`}
      />

      {/* Below `sm:` the rail opens as an overlay over the page rather than
          beside it (see `railOpen`), so it needs a dismiss surface: tapping
          anywhere outside it closes the drawer, same as Escape below. Only
          rendered on a phone with the drawer open -- above `sm:` the rail
          never overlays the page, so there is nothing to dismiss. */}
      {isPhone && drawer && (
        <div
          aria-hidden="true"
          onClick={() => setDrawer(false)}
          className="fixed inset-0 z-[44] bg-slate-900/40"
        />
      )}

      {/* Collapsed it is a column of icons named only by their native title
          tooltip (see AppTab); docked or keyboard-focused it is the full list.
          Focus is done in CSS rather than React state so that pointing at the
          rail does not re-render the page under it. */}
      <aside
        // z-[45] is deliberate, not a round number. The rail is `fixed`, so page
        // content scrolls UNDER it rather than beside it, and the roster grid's
        // sticky header cells sit at z-40 (see ScheduleGrid) -- at z-30 they
        // painted straight over the rail. Above those, still below the z-50
        // dialogs, which should cover everything.
        //
        // `overflow-hidden` is what makes the animation honest: this box is the
        // only thing whose width changes, and it CLIPS the fixed-width content
        // inside rather than squeezing it.
        //
        // Hovering used to widen the rail too (`sm:hover:w-60`), and the 150ms
        // grow clipped the label to whatever the box's current width was
        // mid-animation -- which read as "one or two letters" rather than a
        // name. A native title tooltip on each item (see AppTab) replaces that
        // reveal now, so hover no longer changes this box's width. Keyboard
        // focus still expands it at every width, because tabbing through has
        // no hover and needs an explicit way to see the full list.
        className={`group fixed inset-y-0 left-[env(safe-area-inset-left)] z-[45] flex overflow-hidden border-r border-slate-200 bg-white transition-[width,transform] duration-150 motion-reduce:transition-none dark:border-slate-700 dark:bg-slate-800 ${
          railOpen ? 'w-60' : 'w-14 has-[:focus-visible]:w-60'
        } ${isPhone && !drawer ? 'invisible -translate-x-full' : ''}`}
      >
        {/* THE CONTENT IS 240px WIDE AT EVERY WIDTH -- this is the whole fix.
            The rail animates its width over 150ms; everything inside used to
            switch to its final state on the first frame, so for 150ms the
            title, every label and the entire footer were laid out inside a 56px
            box and then re-flowed as the box grew. That is why the title wrapped
            to two lines and back, and why the theme row, the language select and
            the name overlapped each other.
            Pinning this box at the docked width means the browser lays the rail
            out ONCE. Collapsing only moves the clip edge; nothing inside moves,
            wraps or overlaps, because nothing inside ever changes size. */}
        <div className="flex h-full w-60 shrink-0 flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
        <div className="flex flex-col border-b border-slate-100 dark:border-slate-700">
          {/* One row, not two: the icon, the wordmark and the pin all sit on
              the same line at every width the row is EVER asked to render --
              docked, drawer, hover flyout or keyboard-focused-open.
              `pl-5` equivalent (`pl-2` here + `pl-3` on the Link) still lines
              the brand icon up in the exact same 20px column the pin (and
              every AppTab icon below) sits in, so the icon is what survives
              the clip, not its neighbours. DO NOT GROW THE ICON: 20px + its
              16px + 20px is exactly the 56px of the collapsed `w-14` rail,
              so 16px is what centres it there.

              The pin can't join it on that same 56px line, though -- the icon
              alone already spends 36 of the 56 collapsed pixels, and the pin
              needs ~40 more (its own `px-3 py-2` around a 16px glyph). No
              amount of resizing the wordmark changes that arithmetic, so
              `flex-wrap` plus `basis-full` FORCES the pin onto its own line
              only when collapsed. Expanded (or hover- or focus-widened), the
              row has 240px to work with and the pin sits right after the
              wordmark on the same line. `pl-2` + the pin's own `px-3`
              reproduce the pin's original 20px inset so its glyph still lands
              under the brand icon when wrapped; the extra `pl-3` on the
              icon/wordmark half makes up the remaining 12px so the icon keeps
              its 20px inset too.

              THE ROW HEIGHT IS PINNED AT `h-[72px]`, not left to the content.
              Collapsed wraps to two lines (icon line + pin line); expanded is
              one line. If height tracked content, the header would be
              shorter expanded than collapsed, and every AppTab below it
              would visibly jump up or down by that difference on every
              toggle -- confirmed on a static mock of this exact markup: the
              two-line collapsed block is 56px tall (16px icon line + 8px gap
              + 32px pin line), so 72px (with `content-center` centring that
              block, or the one-line expanded content, inside it) is the
              floor that keeps both states identical. */}
          <div className="flex h-[72px] flex-wrap content-center items-center gap-2 pl-2 pr-2">
            {/* The logo is a header, not a link: the dashboard is the first entry
                of the Info band. */}
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-lg pl-3">
              {/* Icon and wordmark are mutually exclusive: the icon fades via
                  `narrowOnly` like every nav label does (16px, so an
                  invisible-but-present box doesn't move anything). The
                  wordmark instead toggles `hidden`/`flex` -- true removal
                  from layout, not just opacity -- because its `<img>` has an
                  intrinsic height that would otherwise inflate the collapsed
                  line's height even at zero width (measured on a mock: a
                  merely-zero-width span still let its 32px-tall image set
                  the line height, which is what first produced the jump this
                  fixed-height row exists to prevent). */}
              {isCustomer && brandLogo ? (
                <img
                  src={brandLogo}
                  alt=""
                  className={`h-4 w-4 shrink-0 object-contain dark:rounded-sm dark:bg-white ${narrowOnly}`}
                />
              ) : isCustomer ? (
                <span
                  aria-hidden="true"
                  className={`h-4 w-4 shrink-0 text-center text-xs font-bold leading-4 text-slate-800 dark:text-slate-100 ${narrowOnly}`}
                >
                  {BRAND.charAt(0)}
                </span>
              ) : (
                <img
                  src={trgIcon}
                  alt=""
                  className={`h-4 w-4 shrink-0 ${narrowOnly}`}
                />
              )}
              {/* The wordmark is the part that clips -- a 1393x504 image has
                  no legible size that fits in 56px, so it disappears entirely
                  rather than being scaled down into illegibility. Both inks
                  render always; `dark:hidden`/`hidden dark:block` toggle
                  which one is in the layout, because `dark:` sets CSS
                  properties and swapping `src` from JS would flash on load.
                  `h-8` (up from the old `h-7`) roughly matches the pin's own
                  ~32px height so the merged row doesn't look lopsided --
                  measured against a static mock before picking the number;
                  filling the whole available width instead scales the
                  1393x504 source to 68px tall, well past the pinned 72px
                  row. */}
              <span
                className={`min-w-0 flex-1 overflow-hidden ${
                  railOpen ? 'flex' : 'hidden group-has-[:focus-visible]:flex'
                }`}
              >
                {isCustomer && brandLogo ? (
                  <img
                    src={brandLogo}
                    alt={t('org.logo')}
                    className="h-8 w-auto max-w-full object-contain object-left dark:rounded dark:bg-white dark:px-1"
                  />
                ) : isCustomer ? (
                  <span className="truncate text-xl font-semibold leading-8 text-slate-800 dark:text-slate-100">
                    {BRAND}
                  </span>
                ) : (
                  <>
                    <img
                      src={trgWordmarkBlack}
                      alt="TrigTeq"
                      className="h-8 w-auto dark:hidden"
                    />
                    <img
                      src={trgWordmarkWhite}
                      alt="TrigTeq"
                      className="hidden h-8 w-auto dark:block"
                    />
                  </>
                )}
              </span>
            </div>
            <button
              type="button"
              onClick={toggleRail}
              aria-pressed={railToggled}
              title={`${t(railToggled ? 'nav.collapse' : 'nav.expand')} (Ctrl+B)`}
              className={`shrink-0 rounded-lg px-3 py-2 text-slate-500 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-400 dark:hover:bg-slate-700/60 dark:hover:text-slate-200 ${
                railOpen ? 'basis-auto' : 'basis-full group-has-[:focus-visible]:basis-auto'
              }`}
            >
              {railToggled ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeft className="h-4 w-4" />
              )}
              <span className="sr-only">{t(railToggled ? 'nav.collapse' : 'nav.expand')}</span>
            </button>
          </div>
        </div>

        {/* Apps in bands, with each app's own pages indented beneath it on
            hover. Every app is listed from day one, including the ones not
            built yet -- the roadmap belongs in the product, not only in a
            document. Planned entries stay clickable and land on an explanation
            rather than a dead link.

            MODULES ARE NOT DRAWN WHEN COLLAPSED, whatever is hovered: collapsed,
            the rail answers "which app" (see its tooltip), and only a docked
            or drawer-open rail answers "which page" via the flyout below (see
            `openFlyout`'s guard). Only Sales and Roster have any. */}
        <nav
          ref={navScroll}
          aria-label={t('app.title')}
          className="flex-1 overflow-y-auto overflow-x-hidden p-2"
        >
          {NAV_GROUPS.map(({ key, labelKey }) => {
            const apps = visibleApps.filter((a) => a.group === key)
            if (apps.length === 0) return null
            const isFirstVisibleGroup = key === firstVisibleGroupKey
            return (
              <div key={key} className="space-y-0.5">
                {/* The band marker is ONE fixed row at every width, with the
                    heading and the rule stacked in it rather than swapped for
                    each other. A heading needs width it does not have at 56px,
                    so the band shows as a rule between clusters of icons there
                    -- but swapping `display` would change the row's height and
                    shove every tab below it up and down mid-animation.

                    The first group actually drawn sits right under the logo
                    row, so it gets neither the rule nor the heading -- there
                    is nothing above it to separate from or to label. Later
                    markers keep the fixed row height above unconditionally;
                    only this first one is skipped outright. */}
                {!isFirstVisibleGroup && (
                  <div className="relative mt-3 mb-1 h-4">
                    <hr
                      className={`absolute inset-x-2 top-1/2 border-slate-200 dark:border-slate-700 ${narrowOnly}`}
                    />
                    <div
                      className={`absolute inset-x-0 bottom-0 px-3 text-[11px] font-semibold leading-none text-slate-500 dark:text-slate-400 ${wideOnly}`}
                    >
                      {t(labelKey)}
                    </div>
                  </div>
                )}

                {/* SUB-PAGES OPEN TO THE SIDE, never underneath. An in-flow
                    list pushed every icon below it up and down each time one
                    opened, which made the rail move under the pointer -- the
                    one thing a navigation column must never do. A flyout is
                    anchored to its tab and floats over the page, so the icon
                    column is fixed no matter what is open.
                    It has to be a sibling of the rail rather than a child:
                    the rail clips its own contents (that is how the collapse
                    works), and a panel beyond its right edge would be clipped
                    away. Which page you are on is answered by the in-page tab
                    bar instead -- see ModuleTabs. */}
                {apps.map((a) => {
                  const items = flyoutItemsFor(a)
                  // Time and Leave each carry their own approvals badge; My Panel unread notifications; Inventory what waits on OREAS.
                  const Tab =
                    a.key === 'time'
                      ? TimeAppTab
                      : a.key === 'leave'
                        ? LeaveAppTab
                        : a.key === 'me'
                          ? MeAppTab
                          : a.key === 'inventory'
                            ? InventoryAppTab
                            : AppTab
                  return (
                    <div
                      key={a.key}
                      data-app-key={a.key}
                      onMouseEnter={(event) => openFlyout(a.key, event.currentTarget, items)}
                      onMouseLeave={closeFlyoutSoon}
                      // KEYBOARD: focus opens the flyout, ArrowRight moves
                      // into it, Escape or leaving the row closes it. Focus
                      // moving INTO the panel cancels the pending close there.
                      onFocus={(event) => {
                        if (skipFocusOpen.current) skipFocusOpen.current = false
                        else openFlyout(a.key, event.currentTarget, items)
                      }}
                      onBlur={closeFlyoutSoon}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') setFlyout(null)
                        else if (event.key === 'ArrowRight' && flyout?.key === a.key) {
                          event.preventDefault()
                          document.querySelector<HTMLElement>('[data-nav-flyout] a')?.focus()
                        }
                      }}
                    >
                      <Tab
                        to={a.prefix}
                        label={t(a.labelKey)}
                        icon={a.icon}
                        active={activeApp?.key === a.key}
                        labelClass={labelClass}
                      />
                    </div>
                  )
                })}
              </div>
            )
          })}
        </nav>

        {/* THE FOOTER TAKES NO HEIGHT WHEN THE RAIL IS NARROW. It is ~80px of
            controls nobody can see at 56px, and the nav above it is a scroller
            -- so while it held its box, those 80px came straight out of the
            icon list and pushed the last band below the fold. Animated with the
            same 0fr/1fr grid as the sub-pages, so the width and the height
            arrive together and its contents are still laid out at 240px and
            never re-flowed. */}
        <div
          className={`grid shrink-0 transition-[grid-template-rows] duration-150 motion-reduce:transition-none ${
            railOpen
              ? 'grid-rows-[1fr]'
              : 'grid-rows-[0fr] group-has-[:focus-visible]:grid-rows-[1fr]'
          }`}
        >
          <div className="overflow-hidden">
            <div
              className={`space-y-2 border-t border-slate-100 p-3 transition-[opacity,visibility] duration-150 motion-reduce:transition-none dark:border-slate-700 ${footerOnlyWide}`}
            >
              {/* Language and theme share one row: two small settings that are
                  set once and then never touched, so between them they had no
                  claim on two of the three rows down here. Both are the same
                  idiom -- a bordered segmented group -- rather than a select
                  next to a button group, which would have been a second idiom
                  for no reason. `flex-wrap` is the guard for the collapsed
                  rail: two three-segment groups are wider than the one
                  `<select>` this replaced, so at 56px they wrap to their own
                  row instead of being clipped by the footer's `overflow-hidden`. */}
              <div className="flex flex-wrap items-center gap-2">
                <div
                  className="flex shrink-0 rounded-lg border border-slate-300 p-0.5 dark:border-slate-600"
                  role="group"
                  aria-label={t('profile.language')}
                >
                  {LANGUAGES.map(({ code, short, name }) => {
                    const active = i18n.language === code
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => setLanguage(code)}
                        aria-pressed={active}
                        title={name}
                        aria-label={name}
                        className={`rounded-md px-1.5 py-1 text-[11px] font-semibold ${
                          active
                            ? 'bg-blue-600 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
                            : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700/60'
                        }`}
                      >
                        {short}
                      </button>
                    )
                  })}
                </div>
                <div
                  className="flex shrink-0 rounded-lg border border-slate-300 p-0.5 dark:border-slate-600"
                  role="group"
                  aria-label={t('theme.label')}
                >
                  {THEME_MODES.map((mode) => {
                    const Icon = mode === 'light' ? Sun : mode === 'dark' ? Moon : Monitor
                    const active = themeMode === mode
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => chooseTheme(mode)}
                        aria-pressed={active}
                        title={t(`theme.${mode}`)}
                        className={`flex items-center justify-center rounded-md p-1 ${
                          active
                            ? 'bg-blue-600 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
                            : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700/60'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="sr-only">{t(`theme.${mode}`)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 px-1">
                {publicOnly ? (
                  <span className="truncate text-xs text-slate-500 dark:text-slate-400">{user.display_name}</span>
                ) : (
                  <Link
                    to={can(user, 'basic') ? '/me' : '/profile'}
                    className="truncate text-xs text-slate-500 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
                  >
                    {user.display_name}
                  </Link>
                )}
                {host === 'browser' && <SignOutButton host={host} />}
              </div>
            </div>
          </div>
        </div>
        </div>
      </aside>

      {/* The flyout. `fixed`, and outside the rail, so the rail's own clipping
          cannot eat it. Left edge is the docked width: `openFlyout`'s guard
          means one can only open once the rail is already docked or
          drawer-open at that width. z-50 puts it over the page and over the
          rail's sticky neighbours, level with dialogs. */}
      {flyout && (
        <div
          data-nav-flyout=""
          onMouseEnter={keepFlyout}
          onMouseLeave={closeFlyoutSoon}
          onFocus={keepFlyout}
          onBlur={closeFlyoutSoon}
          onKeyDown={(event) => {
            if (event.key === 'Escape' || event.key === 'ArrowLeft') {
              setFlyout(null)
              skipFocusOpen.current = true
              document
                .querySelector<HTMLElement>(`[data-app-key="${flyout.key}"] a`)
                ?.focus({ preventScroll: true })
            }
          }}
          style={{ top: flyout.top }}
          className="fixed left-[calc(244px+env(safe-area-inset-left))] z-50 min-w-44 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
        >
          {flyout.items.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              active={item.icon ? item.to === activeModule(flyout.items) : item.to === `${pathname}${search}`}
            />
          ))}
        </div>
      )}

      <main
        className={`flex min-w-0 flex-1 flex-col ${
          !isPhone
            ? '[--shell-top:var(--banner-height,0px)]'
            : user.impersonated_by_id
              ? '[--shell-top:calc(var(--banner-height,0px)+3rem)]'
              : '[--shell-top:calc(3rem+env(safe-area-inset-top))]'
        }`}
      >
        {/* THE BANNER GOES FIRST, ABOVE EVERYTHING ELSE IN main -- it has to be
            reachable on every route, including ones an impersonated view has
            no nav entry for, since the target's abilities (not the admin's)
            are what /me answers with while this is on. See the component. */}
        <ImpersonationBanner user={user} />
        {isPhone && (
          <div
            className={`sticky z-40 flex items-center border-b border-slate-200 bg-white px-2 dark:border-slate-700 dark:bg-slate-800 ${
              user.impersonated_by_id
                ? 'top-[var(--banner-height,0px)] h-12'
                : 'top-0 h-[calc(3rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)]'
            }`}
          >
            <button
              type="button"
              onClick={() => setDrawer(true)}
              aria-expanded={drawer}
              title={t('nav.expand')}
              className="rounded-lg p-3 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/60"
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">{t('nav.expand')}</span>
            </button>
          </div>
        )}
        {/* The background sign-in check did not complete. NOT a gate: the
            cached user is still right and the page below keeps its state. */}
        {gate === 'shell-stale' && (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-2 bg-amber-100 px-4 py-1.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
          >
            <span>{t('app.sessionCheckFailed')}</span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="rounded-md px-2 py-0.5 font-medium underline hover:bg-amber-200 dark:hover:bg-amber-800/60"
            >
              {t('app.sessionCheckRetry')}
            </button>
          </div>
        )}
        {/* THE MARGIN IS A FUNCTION OF SIDEBAR STATE, not of the route.
            `docked` is the state to read, already computed above -- do not
            build a second notion of pinned. It is false on a phone, which is
            why every varying value in `pageGutterClass` sits behind `lg:`.

            The arithmetic and why `/hr` takes a smaller share of the reclaimed
            rail live in `lib/pageWidth.ts`. No route opts out to zero, and
            pages carry no width caps of their own: this container is the only
            place width is decided.

            THE TRANSITION IS NOT DECORATION. The rail's layout spacer above
            animates its width over 150ms; a bare class swap here would snap
            the gutter to its new value while the rail was still halfway
            through moving, which reads as the content jumping out from under
            the rail rather than the two trading space. `transition-[padding]
            duration-150 motion-reduce:transition-none` is copied off that
            spacer deliberately -- if you retune one duration, retune both. */}
        <div
          className={`min-w-0 flex-1 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] transition-[padding] duration-150 motion-reduce:transition-none ${pageGutterClass(
            pathname,
            docked,
          )}`}
        >
        {/* THE APP'S OWN PAGES, AS TABS ON THE PAGE. The rail answers "which
            app" and its flyout is a preview you have to go looking for; once
            you are inside an app, moving between its pages is the most frequent
            thing you do, and it should not cost a trip back to the rail. This is
            also what tells you which page you are on now that the rail no longer
            keeps a list open. Only Sales and Roster have more than one page. */}
        {pageTabs.length > 1 && activeApp && (
          <ModuleTabs items={pageTabs} activeTo={activeModule(pageTabs)} label={t(activeApp.labelKey)} />
        )}
        {/* Route-level pages are lazy; Splash is already the first-load
            fallback above, so it is the one this reuses. The ErrorBoundary
            sits INSIDE the shell -- around the
            routed content only, not the whole app -- so a chunk-load error
            on navigation shows its fallback with the nav rail still there,
            rather than taking the header and rail blank along with it. */}
        <ErrorBoundary page={activeApp ? t(activeApp.labelKey) : undefined}>
        <Suspense fallback={<Splash inShell />}>
        {publicOnly ? (
          <Routes>
            <Route path="/" element={<PublicDashboard />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
        <Routes>
          {/* The dashboard is the landing page: the state of the business, not
              whichever module happened to be first in the nav. */}
          {/* The dashboard is `basic`, the backend `/api/dashboard` gate.
              `/` STAYS ROUTED for everyone -- the catch-all
              and every landing fall back to it, so leaving it unrouted would
              loop -- and without `basic` it shows Profile, the page this file
              already gives someone who cannot open `/me`. */}
          <Route path="/" element={can(user, 'basic') ? <Dashboard /> : <Profile />} />
          {/* Profile lives inside My Panel; this page is for someone who cannot
              open `/me`. */}
          <Route
            path="/profile"
            element={can(user, 'basic') ? <Navigate to="/me" replace /> : <Profile />}
          />

          {/* A live app's prefix is a real destination -- the tab links to it.
              Without this the tab fell through to the catch-all and bounced
              straight back to the dashboard, which made the whole app
              unreachable. It lands on the first module the user may see, so
              the redirect respects abilities rather than assuming sales. */}
          {/* `/finance` is no longer an app of its own -- its pages are top
              level now -- but it is bookmarked and the old tab pointed at it,
              so it lands on the first of them this user may open. */}
          <Route path="/finance" element={<Navigate to={financeLanding} replace />} />

          {can(user, 'staff') && (
            <Route path="/finance/sales" element={<SalesSection />}>
              <Route index element={<SalesList />} />
              <Route path="new" element={<SalesEditor />} />
              <Route path=":id" element={<SalesEditor />} />
            </Route>
          )}
          {/* Clients and the price list are pages OF Sales now, so they live
              under its prefix. They are registered as siblings of the section
              rather than children of it because they do not share its layout;
              a static segment outranks `:id`, so `/finance/sales/clients` is
              never read as a document id. The old top-level URLs redirect --
              they are bookmarked, and the old tabs pointed at them. */}
          {can(user, 'staff') && (
            <Route path="/finance/sales/clients" element={<Clients />} />
          )}
          {can(user, 'staff') && (
            <Route path="/finance/sales/clients/:id" element={<ClientDetail />} />
          )}
          {can(user, 'staff') && (
            <Route path="/finance/sales/price-list" element={<PriceList />} />
          )}
          {/* The company bank list is a Sales tab,
              gated `finance` ONLY -- the same ability `routers/bank.py` puts on
              every write, and the `banks` module's gate in `lib/apps.ts`. A
              sibling like Clients, so the static segment outranks `:id`. */}
          {can(user, 'payroll') && <Route path="/finance/sales/banks" element={<Banks />} />}
          <Route path="/finance/clients" element={<Navigate to="/finance/sales/clients" replace />} />
          <Route
            path="/finance/price-list"
            element={<Navigate to="/finance/sales/price-list" replace />}
          />
          {can(user, 'staff') && (
            <Route path="/finance/activity-expense" element={<ActivityFund />} />
          )}
          {/* Trips are a tab inside Requests; the old URL keeps working. */}
          {can(user, 'staff') && <Route path="/finance/requests" element={<RequestsHub />} />}
          {can(user, 'staff') && (
            <Route path="/finance/trips" element={<Navigate to="/finance/requests?tab=trips" replace />} />
          )}

          {/* Org chart, HR and Inventory are pages of their own: the org chart
              open to every signed-in user, HR on `hr.view` (see
              `lib/hrAccess.ts`), Inventory on the server's reader flag. */}
          <Route path="/organization" element={<OrganizationPage />} />
          <Route path="/org-chart" element={<Navigate to="/organization?tab=orgchart" replace />} />
          <Route path="/hr" element={<Navigate to="/organization?tab=directory" replace />} />
          {/* HR is one page: the structure above, the staff list below.
              `/hr/new` falls through to `/hr/:id`, where the editor reads the
              id "new" as "this is a new record". `/hr/users` has its own
              static route below, which outranks `:id`. A reader without
              `hr.edit` gets `/hr/new` as a redirect to the list instead of a
              blank read-only form. */}
          {mayOpenHr(user) && (
            <>
              {!mayEditHr(user) && <Route path="/hr/new" element={<Navigate to="/hr" replace />} />}
              <Route path="/hr/:id" element={<EmployeeDetail />} />
            </>
          )}
          {/* The inventory admin's only, reads included. */}
          {readsInventory(user) && <Route path="/inventory" element={<Laptops />} />}
          <Route path="/inventory/laptops" element={<Navigate to="/inventory" replace />} />
          {/* The four issuer identities, read-only: the one member-facing read
              of them (editing is `admin/Organization`). Nothing in the nav
              links here; `/company/info` reaches it through
              `CompanyInfoRedirect`. */}
          {can(user, 'staff') && <Route path="/company-info" element={<Company />} />}
          {/* The retired `/company` page's URLs, and the pre-tabs HR ones
              (`/hr/directory/...`), bookmarked and still built by
              notifications (`entityRoute` -> `/hr/<employeeId>`, which is a
              live route again). Ungated: the TARGET route carries the ability,
              so a viewer without it lands where the catch-all sends them. */}
          <Route path="/company/info" element={<CompanyInfoRedirect canOpenOrg={can(user, 'admin')} />} />
          <Route path="/company" element={<LegacyRedirect />} />
          <Route path="/company/*" element={<LegacyRedirect />} />
          <Route path="/hr/directory" element={<LegacyRedirect />} />
          <Route path="/hr/directory/*" element={<LegacyRedirect />} />
          <Route path="/hr/org-chart" element={<LegacyRedirect />} />

          {/* Time logging and Leave are two top-level apps, each a single page
              -- not two modules under one "Time Management" heading. Their old
              nested URLs still resolve: they are bookmarked, and a Teams deep
              link may still point at them. */}
          {/* Time logging is `basic`, matching its nav entry. */}
          {can(user, 'self.log') && <Route path="/time" element={<TimeLogging />} />}
          {can(user, 'self.log') && <Route path="/leave" element={<Leave />} />}
          {user.contractor_log === true && <Route path="/contractor-log" element={<ContractorLog />} />}
          <Route path="/time/logging" element={<Navigate to="/time" replace />} />
          <Route path="/time/leave" element={<Navigate to="/leave" replace />} />

          {/* Administration. Platform accounts and the time-module corrections,
              gathered out of HR and Time. Each route is gated with the same
              expression as its AppDef in lib/apps.ts -- nav and routing drifting
              apart is how a hidden tab stays reachable by typing its URL.

              BY ABILITY, NOT BY `user.is_admin`. These five were one
              `user.is_admin &&` block, which is what made "HR administers leave
              but never touches accounts" impossible to say. `can()` returns true
              for the admin tier whatever the ability, so an administrator still
              sees all five; a level holding `time.admin` now reaches the three
              pages its grant already unlocked on the API.
              `/admin` is no longer an app of its own -- its five pages are top
              level now -- but it is bookmarked and the old tab pointed at it, so
              it lands on the first of them this user may open, which differs: an
              administrator gets Users, a leave administrator gets Leave
              corrections. The other old URLs still resolve for the same reason. */}
          <Route path="/admin" element={<Navigate to={adminLanding} replace />} />
          {/* The admin section (Users, Config, Audit,
              Automation) is admin ONLY -- the `admin` ability, same as each
              entry in lib/apps.ts, and the same as their APIs. */}
          {can(user, 'admin') && (
            <>
              <Route path="/admin/users" element={<AdminUsers />} />
              {/* The payroll access log is a tab inside /payroll (same admin gate),
                  so it has no route here; `app.adminAuditLog` is only
                  `Payroll.tsx`'s tab label. */}
              {/* THREE ROUTES, ONE PAGE, THREE TABS -- the change record, the
                  admin-actions log and the per-person trail (see `Audit.tsx`).
                  `/admin/audit` names the whole page; the other two redirect
                  here with a `?tab=`, below, so no bookmark 404s.

                  ONE GATE FOR ALL THREE, AND IT IS THE API'S OWN:
                  `routers/audit.py` requires `admin` on the whole router, so
                  `Audit.tsx` does NOT re-check it per tab the way `Config.tsx`
                  must. Routing it any wider would render a permission error
                  instead of a closed page. */}
              <Route path="/admin/audit" element={<Audit />} />
            </>
          )}
          {/* One page of tabs -- Config.tsx's header holds the tab count and
              the full ability map; do not restate it here. `canAny` here
              is the OR of every ability any one of its tabs needs; `Config.tsx`
              gates each tab again internally on its own ability, so a route
              open to a `time.admin`-only holder still renders only the tabs
              that one may actually use, never all of them. Same `canAny` as its
              AppDef in `lib/apps.ts` (`anyAbility`), for the reason the block
              comment above gives: nav and routing must not drift apart. */}
          {/* Admin, or the calendar keeper. Anyone else opening an old `?tab=rates` /
              `?tab=banks` bookmark is sent to where that tab went;
              anything else falls to the dashboard, as an unrouted URL would. */}
          <Route
            path="/admin/config"
            element={mayOpenConfig(user) ? <Config /> : <ConfigMovedRedirect />}
          />
          {/* No `/admin/banks` route: the bank list is a tab of `/admin/config`
              above, reached through that route's guard, and `Config.tsx`
              re-gates the tab on the ability `routers/bank.py` puts on every
              write. The old route redirects below. */}
          <Route path="/admin/approval-backlog" element={<Navigate to={BACKLOG_TAB_HREF} replace />} />
          {/* Same ability as the nav tab in lib/apps.ts, for the reason above. */}
          {can(user, 'admin') && (
            <Route path="/admin/automation" element={<Automation />} />
          )}
          <Route path="/hr/users" element={<Navigate to="/admin/users" replace />} />
          {/* The old correction URLs land directly in each page's own admin
              scope. */}
          <Route path="/time/admin" element={<Navigate to="/time?scope=admin" replace />} />
          <Route path="/admin/time" element={<Navigate to="/time?scope=admin" replace />} />
          <Route path="/admin/time-logs" element={<Navigate to="/time?scope=admin" replace />} />
          <Route path="/admin/leave" element={<Navigate to={leaveHref('admin')} replace />} />
          {/* Activities, Calendar, Organization and OTP are `/admin/config`
              tabs. The old routes redirect unconditionally: the destination
              page gates itself, which is why these are not wrapped in the
              `canAny` check above. */}
          <Route
            path="/admin/activities"
            element={<Navigate to="/admin/config?tab=activities" replace />}
          />
          <Route
            path="/admin/calendar"
            element={<Navigate to="/admin/config?tab=calendar" replace />}
          />
          <Route
            path="/admin/organization"
            element={<Navigate to="/admin/config?tab=org" replace />}
          />
          <Route
            path="/admin/otp"
            element={<Navigate to="/admin/config?tab=otp" replace />}
          />
          {/* Unconditional for the same reason: the destination gates itself.
              `/admin/banks` may be bookmarked or a Teams deep link, and the
              catch-all would read as a broken link. */}
          <Route
            path="/admin/banks"
            element={<Navigate to="/finance/sales/banks" replace />}
          />
          {/* The change record and the per-person trail are `/admin/audit`
              tabs. Unconditional for the same reason as above -- the
              destination gates itself, so a viewer without `admin` falls
              through to the dashboard rather than a 404.

              `/admin/changes` carries no `?tab=` because the change record is
              already the default tab -- adding `?tab=changes` would put a
              redundant key in the URL bar of everyone arriving from the old
              bookmark. */}
          <Route path="/admin/changes" element={<Navigate to="/admin/audit" replace />} />
          <Route
            path="/admin/person-trail"
            element={<Navigate to="/admin/audit?tab=person" replace />}
          />

          {/* Roster. The app prefix is a real destination -- the tab links to
              it -- so it redirects to the first module the user may see rather
              than falling through to the catch-all and bouncing to the
              dashboard. All six modules share one document, so they hang off a
              section route that owns it. */}
          {/* Schedule is every internal member's, read-only; the other pages need a roster role, and without one any other roster URL lands on Schedule.
              No schedule at all: the page says why with the server's reason instead of bouncing home in silence. */}
          <Route
            path="/roster"
            element={
              !scheduleOk ? (
                <NoRosterRole reason={user.roster_access?.reason} />
              ) : rosterPhoneTarget ? (
                <Navigate to={rosterPhoneTarget} replace />
              ) : (
                <RosterSection />
              )
            }
          >
            <Route index element={<Navigate to={landing('roster')} replace />} />
            <Route path="schedule" element={<Schedule />} />
            {/* One's own days, for everyone who reaches the roster. */}
            <Route path="my" element={<MySchedule user={user} embedded />} />
            {rosterOk ? (
              <>
                <Route path="people" element={<RosterPeople />} />
                <Route path="bus" element={<Bus />} />
                <Route path="hse" element={<Hse />} />
                {(holdsTick(user, 'assets') || isRosterManager(user)) && <Route path="gear" element={<RosterGear />} />}
                <Route path="performance" element={<Performance />} />
                <Route path="cost" element={<Cost />} />
                <Route path="projects" element={<RosterSetup />} />
                {/* An old Setup link still lands on Projects. */}
                <Route path="setup" element={<Navigate to="/roster/projects" replace />} />
              </>
            ) : (
              <Route path="*" element={<Navigate to="/roster/schedule" replace />} />
            )}
          </Route>

          {/* Single-page apps: everything they do opens as a popup over one
              route, so there is no module list and no index redirect. */}
          {can(user, 'self.log') && <Route path="/hot-desk" element={<HotDesk />} />}
          {/* The Outlook calendar is a Dashboard section; old links land there. */}
          <Route path="/calendar" element={<Navigate to="/" replace />} />
          <Route path="/calendar/company" element={<Navigate to="/" replace />} />

          {/* `salary.view` OR `admin`: the payroll access log is an `admin` tab,
              and a level can hold `admin` without `salary.view`. */}
          {(can(user, 'salary.view') || can(user, 'admin')) && (
            <Route path="/payroll" element={<Payroll />} />
          )}

          {/* Your own payslips, and deliberately NOT behind the gate on the
              line above. `salary.view` is the right to read the company's pay;
              an employee reading their own holds nothing of the sort, and
              granting it to give them a payslip would hand them everyone's.
              What makes this safe is not a check but the shape of the API it
              calls -- `/api/me/payslips` reads the person off the session and
              takes no employee id anywhere -- so there is no request this page
              could make about a colleague. See `routers/payslip_portal.py`. */}
          {/* My Panel is ONE page and `basic`, so the contractor reaches it;
              its data is self-scoped by the API's shape. The retired tab URLs
              redirect (`legacyMePath`). */}
          {can(user, 'basic') && <Route path="/me" element={<MyPanel />} />}
          {can(user, 'staff') && (
            <>
              <Route path="/me/salary" element={<Navigate to={legacyMePath('/me/salary')!} replace />} />
              <Route path="/me/info" element={<Navigate to={legacyMePath('/me/info')!} replace />} />
              <Route path="/me/supervisor" element={<Navigate to={legacyMePath('/me/supervisor')!} replace />} />
              <Route path="/me/equipment" element={<Navigate to={legacyMePath('/me/equipment')!} replace />} />
            </>
          )}
          <Route path="/my-payslips" element={<Navigate to={legacyMePath('/my-payslips')!} replace />} />

          {/* Apps whose pages are not built yet resolve to an explanation
              rather than a redirect that reads as a broken link. Every app in
              the registry is `live` today, so these render nothing -- they are
              here for the next module, not for a current one. A page that
              exists must never be listed as anything but `live`, or the
              placeholder below shadows it. */}
          {APPS.filter((a) => a.status !== 'live').map((a) => (
            <Route key={a.key} path={`${a.prefix}/*`} element={<ComingSoon app={a} />} />
          ))}
          {APPS.filter((a) => a.status !== 'live').map((a) => (
            <Route key={`${a.key}-root`} path={a.prefix} element={<ComingSoon app={a} />} />
          ))}

          {UiKitGallery && <Route path="/dev/ui-kit" element={<UiKitGallery />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        )}
        </Suspense>
        </ErrorBoundary>
        </div>
      </main>

    </div>
  )
}

/**
 * The one <Toaster>, mounted ABOVE every screen App can return -- the sign-in
 * screen, the forced password change, the error screens and the signed-in
 * shell. It used to sit inside the shell only, so a toast raised before sign-in
 * (a wrong emailed code) was never seen. The theme is read back from the
 * <html data-theme> stamp that `applyTheme` writes, because the theme state
 * lives inside AppShell.
 */
function RootToaster() {
  const read = () => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light')
  const [theme, setTheme] = useState<'dark' | 'light'>(read)
  useEffect(() => {
    setTheme(read())
    const observer = new MutationObserver(() => setTheme(read()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])
  return <Toaster position="top-center" richColors theme={theme} mobileOffset={{ top: 'max(16px, env(safe-area-inset-top))' }} />
}

export default function App() {
  const [shareToken] = useState(() => shareTokenFromPath(window.location.pathname))
  const [stockMode] = useState(() => publicStockMode(window.location.pathname))
  if (shareToken) return <ShareOpen token={shareToken} />
  if (stockMode) {
    return (
      <Suspense fallback={null}>
        <OreasStock mode={stockMode} />
      </Suspense>
    )
  }
  return (
    <>
      <AppShell />
      <RootToaster />
    </>
  )
}

/**
 * One app in the sidebar.
 *
 * EVERY TAB LOOKS THE SAME unless it is the current one. This used to dim any
 * app whose `status` was not 'live', which greyed four of the six and made the
 * list read as half-disabled -- while `BUILT` in this file already lists every
 * one of those apps as having real pages, so none of them were actually
 * unavailable. A tab you can click and use should not look switched off. An
 * app that genuinely has no pages still resolves to ComingSoon, which says so
 * on the page rather than in a shade of grey.
 */
function AppTab({
  to,
  label,
  icon: Icon,
  active,
  labelClass,
  badge,
  title,
}: {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  active: boolean
  /** How the text behaves at the rail's current width — see `labelClass` in App. */
  labelClass: string
  /** A count to ride on the icon, or `null`/undefined for none. */
  badge?: string | null
  /** Overrides `label` as the native tooltip while a badge shows. */
  title?: string
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      // The native tooltip is what names the icon when the rail is collapsed
      // and the pointer is not moving, which is the state it spends most of
      // its life in.
      title={title ?? label}
      className={`group/tab relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-150 motion-reduce:transition-none hover:bg-slate-100 dark:hover:bg-slate-700/60 ${
        active
          ? 'bg-blue-50 font-medium text-blue-700 before:absolute before:inset-y-1.5 before:-left-2 before:w-[3px] before:rounded-r-full before:bg-blue-600 dark:bg-blue-950/40 dark:text-blue-300 dark:before:bg-blue-400'
          : 'text-slate-700 dark:text-slate-200'
      }`}
    >
      {/* The count rides ON the glyph, not beside it: beside it, the number
          would be clipped away with the label at the rail's collapsed 56px. */}
      <span className="relative shrink-0">
        <Icon
          className={`h-4 w-4 ${
            active
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-slate-500 group-hover/tab:text-slate-700 dark:text-slate-400 dark:group-hover/tab:text-slate-200'
          }`}
        />
        {badge && (
          <span className="absolute -right-2 -top-1.5 min-w-4 rounded-full bg-red-600 px-1 text-center text-[0.6rem] font-semibold leading-4 text-white dark:bg-red-500">
            {badge}
          </span>
        )}
      </span>
      <span className={`truncate ${labelClass}`}>{label}</span>
    </Link>
  )
}

/** The Time nav item, with the supervisee timesheet days awaiting this viewer on its icon; zero renders no chip (`navBadgeText`). */
function TimeAppTab(props: Omit<Parameters<typeof AppTab>[0], 'badge' | 'title'>) {
  const { t } = useTranslation()
  const count = useApprovalsAwaiting().time
  const badge = navBadgeText(count)
  return (
    <AppTab
      {...props}
      badge={badge}
      title={badge ? t('nav.timeAwaiting', { count }) : undefined}
    />
  )
}

/** The Leave nav item, with the leave requests awaiting this viewer's decision on its icon. */
function LeaveAppTab(props: Omit<Parameters<typeof AppTab>[0], 'badge' | 'title'>) {
  const { t } = useTranslation()
  const count = useApprovalsAwaiting().leave
  const badge = navBadgeText(count)
  return (
    <AppTab
      {...props}
      badge={badge}
      title={badge ? t('nav.leaveAwaiting', { count }) : undefined}
    />
  )
}

/** The Inventory nav item, with what waits for the person assigned to OREAS or their stand-in; with a chip it opens what the chip counts. */
function InventoryAppTab(props: Omit<Parameters<typeof AppTab>[0], 'badge' | 'title'>) {
  const { t } = useTranslation()
  const { data: user } = useMe()
  const count = useOreasAwaiting(canWriteOreas(user))
  const badge = navBadgeText(count)
  return (
    <AppTab
      {...props}
      to={badge ? OREAS_REQUESTS_LINK : props.to}
      badge={badge}
      title={badge ? t('nav.oreasAwaiting', { count }) : undefined}
    />
  )
}

/** The My Panel nav item, with the unread-notification count on its icon.
 *  Same `navBadgeText` rule as the Time item (0 shows nothing, over 99 shows
 *  `99+`), a DIFFERENT number -- never the approvals count. */
function MeAppTab(props: Omit<Parameters<typeof AppTab>[0], 'badge' | 'title'>) {
  const { t } = useTranslation()
  const { count } = useUnreadNotifications()
  const badge = navBadgeText(count)
  return (
    <AppTab
      {...props}
      badge={badge}
      title={badge ? t('nav.notificationsUnread', { count }) : undefined}
    />
  )
}

/**
 * The active app's pages, as a tab strip across the top of the page.
 *
 * The counterpart to the rail: the rail is for choosing an app, this is for
 * moving around inside the one you are in. It also carries "where am I", which
 * the rail used to answer with an indented list and no longer does.
 */
function ModuleTabs({
  items,
  activeTo,
  label,
}: {
  items: NavItemDef[]
  activeTo: string | undefined
  label: string
}) {
  return (
    <nav
      aria-label={label}
      className="scrollbar-none mb-4 flex gap-1 overflow-x-auto shadow-[inset_0_-1px_0_var(--color-border-subtle)]"
    >
      {items.map((item) => {
        const Icon = item.icon
        const active = item.to === activeTo
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={active ? 'page' : undefined}
            // The strip's line is an inset shadow so the active underline covers it; a border plus -mb-px overflows the scroller by 1px.
            className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm ${
              active
                ? 'border-blue-600 font-medium text-blue-700 dark:border-blue-400 dark:text-blue-300'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

function NavItem({
  to,
  label,
  icon: Icon,
  active,
}: {
  to: string
  label: string
  icon?: React.ComponentType<{ className?: string }>
  /** Decided by the caller — see `activeModule` in App, which picks the longest
   *  matching module path rather than any prefix match. */
  active: boolean
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700/60 ${
        active
          ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
          : 'text-slate-700 dark:text-slate-200'
      }`}
    >
      {Icon && (
        <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`} />
      )}
      {label}
    </Link>
  )
}

/**
 * A URL the app used to have (`/company/hr/<id>`, `/hr/directory/<id>`) to its
 * current page, carrying the id, the query string and the hash. Anything the
 * map does not know goes where the catch-all would have sent it.
 */
function LegacyRedirect() {
  const { pathname, search, hash } = useLocation()
  const target = legacyCompanyPath(pathname)
  return <Navigate to={target ? `${target}${search}${hash}` : '/'} replace />
}

/** `/company/info` was the read-only issuer identities. Whoever may open
 *  Admin > Organization goes on to the editable page; everyone else gets the
 *  read-only view at `/company-info`. */
function CompanyInfoRedirect({ canOpenOrg }: { canOpenOrg: boolean }) {
  return <Navigate to={canOpenOrg ? '/admin/config?tab=org' : '/company-info'} replace />
}

function Splash({ inShell = false }: { inShell?: boolean }) {
  return (
    <div
      className={
        inShell
          ? 'flex items-center justify-center py-6'
          : 'flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900'
      }
    >
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-700 dark:border-slate-600 dark:border-t-blue-400" />
    </div>
  )
}


function TeamsAuthError({ detail, onRetry }: { detail?: string; onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-900">
      <div className="max-w-md space-y-3 rounded-xl border border-red-200 bg-white p-6 dark:border-red-900 dark:bg-slate-800">
        <h1 className="font-semibold text-slate-800 dark:text-slate-100">Teams sign-in did not complete</h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          The tab loaded but could not obtain a token. This is usually one of: the app
          registration's <code>webApplicationInfo</code> does not match the manifest, admin consent
          for <code>access_as_user</code> has not been granted, or the account has no {BRAND}
          user record.
        </p>
        {detail && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {/* The server's own reason, so support is not left guessing. */}
            {detail}
          </p>
        )}
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg bg-slate-700 px-3 py-2 text-sm text-white hover:bg-slate-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          Retry
        </button>
      </div>
    </div>
  )
}

/**
 * The API is not answering -- it is down, unreachable, or erroring. Explicitly
 * NOT an account problem, so it must not tell the user to contact an admin
 * about their account.
 */
function ApiUnreachableError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-900">
      <div className="max-w-md space-y-3 rounded-xl border border-red-200 bg-white p-6 dark:border-red-900 dark:bg-slate-800">
        <h1 className="font-semibold text-slate-800 dark:text-slate-100">{t('app.apiDownTitle')}</h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {t('app.apiDownBody')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg bg-slate-700 px-3 py-2 text-sm text-white hover:bg-slate-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          {t('app.sessionCheckRetry')}
        </button>
      </div>
    </div>
  )
}

function NoAccountError({
  detail,
  notSetUpAddress = null,
}: {
  detail?: string
  /** The address from an `account_not_set_up` refusal ('' when the token
   *  carried none), or null for any other 403 -- see lib/accountNotSetUp. */
  notSetUpAddress?: string | null
}) {
  const { t } = useTranslation()
  // An unknown Microsoft account gets its own translated sentence naming
  // the address to give HR; a refusal with no sentence of ours keeps the server's wording.
  const notSetUp =
    notSetUpAddress === null
      ? null
      : notSetUpAddress
        ? t('otp.accountNotSetUp', { address: notSetUpAddress })
        : t('otp.accountNotSetUpNoAddress')
  const companyOff = isCompanyDeactivated(detail) ? t('password.refusalCompanyDeactivated') : null
  const inactive = isAccountInactive(detail) ? t('otp.noAccountInactive') : null
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-900">
      <div className="max-w-md space-y-3 rounded-xl border border-amber-300 bg-white p-6 dark:border-amber-900 dark:bg-slate-800">
        <h1 className="font-semibold text-slate-800 dark:text-slate-100">{t('otp.noAccountTitle')}</h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {/* The server says why -- outside the organisation, inactive, or
              provisioning turned off. Its wording beats a generic sentence. */}
          {notSetUp ||
            companyOff ||
            inactive ||
            detail ||
            t('otp.noAccountFallback')}
        </p>
        <button
          type="button"
          onClick={() => {
            void signOutEverywhere().finally(() => window.location.reload())
          }}
          className={btnGhost}
        >
          {t('otp.noAccountSwitch')}
        </button>
      </div>
    </div>
  )
}

/** `/admin/config` for a viewer who may not open it : follow a tab
 *  that moved out (`movedConfigTab`), otherwise the dashboard. */
function ConfigMovedRedirect() {
  const { search } = useLocation()
  const target = movedConfigTab(new URLSearchParams(search).get('tab'))
  return <Navigate to={target ?? '/'} replace />
}
